// Sistema de Rastreamento Automático de Leads
// Captura automaticamente informações de origem, UTMs e dados de conversão

import { saveLead } from '../modules/leads-system/utils';

export interface LeadData {
  id: string;
  nome: string;
  telefone?: string;
  email?: string;
  empresa?: string;
  servicoInteresse: string;
  paginaOrigem: string;
  dataHora: string;
  origem: 'WhatsApp' | 'Formulário' | 'Comunidade' | 'Análise Gratuita' | 'Contato';
  botaoClicado: string;
  responsavel: string;
  observacoes: string;
  status: 'novo' | 'contatado' | 'qualificado' | 'proposta' | 'ganho' | 'perdido';
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

// Captura parâmetros UTM da URL
export function captureUTMParams(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utmSource: urlParams.get('utm_source') || undefined,
    utmMedium: urlParams.get('utm_medium') || undefined,
    utmCampaign: urlParams.get('utm_campaign') || undefined,
  };
}

// Salva lead no Supabase
export async function saveLeadTracking(lead: Omit<LeadData, 'id' | 'dataHora'>): Promise<void> {
  try {
    await saveLead({
      nome: lead.nome,
      email: lead.email || '',
      telefone: lead.telefone,
      servicoInteresse: lead.servicoInteresse,
      paginaOrigem: lead.paginaOrigem,
      origem: lead.origem,
      botaoClicado: lead.botaoClicado,
      responsavel: lead.responsavel,
      observacoes: lead.observacoes,
      status: lead.status,
      utmSource: lead.utmSource,
      utmMedium: lead.utmMedium,
      utmCampaign: lead.utmCampaign,
    });
  } catch (error) {
    console.error('Erro ao salvar lead:', error);
  }
}

// Recupera todos os leads (DEPRECATED - usar loadLeads do módulo)
export function getLeads(): LeadData[] {
  console.warn('getLeads() está deprecated. Use loadLeads() do módulo leads-system');
  try {
    const leadsJson = localStorage.getItem('ibx_leads');
    return leadsJson ? JSON.parse(leadsJson) : [];
  } catch (error) {
    console.error('Erro ao recuperar leads:', error);
    return [];
  }
}

// Registra clique em botão WhatsApp
export async function trackWhatsAppClick(
  buttonLabel: string,
  servicoInteresse: string = 'Não especificado'
): Promise<void> {
  const utmParams = captureUTMParams();
  
  await saveLeadTracking({
    nome: 'Lead via WhatsApp',
    servicoInteresse,
    paginaOrigem: window.location.pathname,
    origem: 'WhatsApp',
    botaoClicado: buttonLabel,
    responsavel: '',
    observacoes: 'Lead gerado via clique no botão WhatsApp',
    status: 'novo',
    ...utmParams,
  });
}

// Registra submissão de formulário
export async function trackFormSubmission(
  formData: {
    nome: string;
    email: string;
    telefone?: string;
    empresa?: string;
    servicoInteresse?: string;
    mensagem?: string;
  },
  formType: 'Formulário' | 'Comunidade' | 'Análise Gratuita' | 'Contato',
  buttonLabel: string
): Promise<void> {
  const utmParams = captureUTMParams();
  
  await saveLeadTracking({
    nome: formData.nome,
    email: formData.email,
    telefone: formData.telefone,
    empresa: formData.empresa,
    servicoInteresse: formData.servicoInteresse || 'Não especificado',
    paginaOrigem: window.location.pathname,
    origem: formType,
    botaoClicado: buttonLabel,
    responsavel: '',
    observacoes: formData.mensagem || '',
    status: 'novo',
    ...utmParams,
  });
}

// Registra clique em CTA (Call to Action)
export async function trackCTAClick(
  ctaLabel: string,
  servicoInteresse: string = 'Não especificado',
  destination: string
): Promise<void> {
  const utmParams = captureUTMParams();
  
  await saveLeadTracking({
    nome: 'Lead via CTA',
    servicoInteresse,
    paginaOrigem: window.location.pathname,
    origem: destination.includes('wa.me') ? 'WhatsApp' : 'Contato',
    botaoClicado: ctaLabel,
    responsavel: '',
    observacoes: `Clique em CTA: ${ctaLabel} → ${destination}`,
    status: 'novo',
    ...utmParams,
  });
}

// Limpa leads antigos (opcional - manter apenas últimos 90 dias)
export function cleanOldLeads(daysToKeep: number = 90): void {
  console.warn('cleanOldLeads() não é mais necessário com Supabase. Use políticas de retenção no banco de dados.');
}